import React, { useState, useEffect } from 'react';
import { X, Clock, Activity, User, Globe, Loader2, Monitor, RefreshCw, ExternalLink, Mail } from 'lucide-react';
import { fetchSessionAnalytics } from '../api/analyticsApi';

const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
    const h = Math.floor(m / 60);
    const remM = m % 60;
    return remM ? `${h}h ${remM}m` : `${h}h`;
};

const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

const truncateUrl = (url) => {
    if (!url) return null;
    try {
        const u = new URL(url);
        const path = `${u.pathname}${u.search}`;
        return { host: u.host, path: path === '/' ? '' : path, full: url };
    } catch {
        return { host: url, path: '', full: url };
    }
};

const prettyEvent = (name) => {
    if (!name) return '—';
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const SessionDetailModal = ({ isOpen, onClose, startDate, endDate }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        total_sessions: 0,
        avg_session_duration_seconds: 0,
        unique_buyers: 0,
        authenticated_sessions: 0,
        anonymous_sessions: 0,
        sessions: [],
    });

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const resp = await fetchSessionAnalytics(startDate, endDate, 500);
            setData({
                total_sessions: resp?.total_sessions ?? 0,
                avg_session_duration_seconds: resp?.avg_session_duration_seconds ?? 0,
                unique_buyers: resp?.unique_buyers ?? 0,
                authenticated_sessions: resp?.authenticated_sessions ?? 0,
                anonymous_sessions: resp?.anonymous_sessions ?? 0,
                sessions: Array.isArray(resp?.sessions) ? resp.sessions : [],
            });
        } catch (err) {
            console.error('Error loading session analytics:', err);
            setError('Failed to load session analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, startDate, endDate]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-5 sm:p-6 border-b border-gray-200">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Monitor className="w-5 h-5 text-blue-600" />
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Buyer Portal Sessions</h2>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            All sessions in the selected date range
                        </p>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                            onClick={load}
                            disabled={loading}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Summary strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div>
                        <p className="text-xs text-gray-500">Total Sessions</p>
                        <p className="text-lg font-bold text-gray-900">{data.total_sessions.toLocaleString()}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Avg Duration</p>
                        <p className="text-lg font-bold text-gray-900">{formatDuration(data.avg_session_duration_seconds)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Unique Buyers</p>
                        <p className="text-lg font-bold text-gray-900">{data.unique_buyers.toLocaleString()}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Auth / Anon</p>
                        <p className="text-lg font-bold text-gray-900">
                            {data.authenticated_sessions}
                            <span className="text-sm text-gray-400"> / {data.anonymous_sessions}</span>
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : data.sessions.length === 0 ? (
                        <div className="text-center text-gray-500 py-12">
                            <Monitor className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="font-medium text-gray-700">No sessions in this range</p>
                            <p className="text-sm text-gray-500 mt-1">Try a different date range.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-semibold">Buyer</th>
                                        <th className="px-3 py-2 text-left font-semibold">Started</th>
                                        <th className="px-3 py-2 text-left font-semibold">Duration</th>
                                        <th className="px-3 py-2 text-right font-semibold">Events</th>
                                        <th className="px-3 py-2 text-left font-semibold">Last Event</th>
                                        <th className="px-3 py-2 text-left font-semibold">Page</th>
                                        <th className="px-3 py-2 text-left font-semibold">Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.sessions.map((s) => {
                                        const url = truncateUrl(s.page_url);
                                        return (
                                            <tr key={s.session_id} className="hover:bg-gray-50">
                                                <td className="px-3 py-2.5">
                                                    {s.is_authenticated && s.buyer ? (
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-1.5 font-medium text-gray-900 truncate">
                                                                <User className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                                                <span className="truncate">{s.buyer.name}</span>
                                                            </div>
                                                            {s.buyer.email && (
                                                                <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
                                                                    <Mail className="w-3 h-3 flex-shrink-0" />
                                                                    <span className="truncate">{s.buyer.email}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                                                            Anonymous
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                                                    {formatDateTime(s.session_start)}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <span className="inline-flex items-center gap-1 font-medium text-gray-900">
                                                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                                                        {formatDuration(s.duration_seconds)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-right">
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
                                                        <Activity className="w-3 h-3" />
                                                        {s.event_count}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                                                    {prettyEvent(s.last_event_name)}
                                                </td>
                                                <td className="px-3 py-2.5 max-w-xs">
                                                    {url ? (
                                                        <a
                                                            href={url.full}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 text-blue-600 hover:underline truncate"
                                                            title={url.full}
                                                        >
                                                            <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                                                            <span className="truncate">{url.host}{url.path}</span>
                                                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                                                    {s.utm_source || <span className="text-gray-400">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionDetailModal;
